/**
 * InviteUserForm.tsx — invite a user (T-411, Slice 1).
 *
 * Deliberately renders and stays usable even while the list is loading or has failed: inviting
 * does not depend on the list, and an admin who came here to add someone should not be blocked
 * by an unrelated read.
 *
 * The copy states the temp-password window because that promise has to be made somewhere the
 * admin sees it — the value comes from the server (one constant shared with the pool setting and
 * the invite email), never hardcoded here.
 */
import { useState } from 'react'

interface InviteUserFormProps {
  validityDays: number
  busy: boolean
  onInvite: (input: { email: string; makeAdmin: boolean; note?: string }) => void
}

export function InviteUserForm({ validityDays, busy, onInvite }: InviteUserFormProps) {
  const [email, setEmail] = useState('')
  const [makeAdmin, setMakeAdmin] = useState(false)

  const canSubmit = email.trim().length > 0 && !busy

  return (
    <form
      className="space-y-2 rounded-md border border-border p-3"
      onSubmit={(e) => {
        e.preventDefault()
        if (!canSubmit) return
        onInvite({ email: email.trim(), makeAdmin })
        setEmail('')
        setMakeAdmin(false)
      }}
    >
      <div>
        <label className="text-sm font-medium text-foreground" htmlFor="invite-email">
          Email address
        </label>
        <input
          id="invite-email"
          type="email"
          autoComplete="off"
          className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground placeholder:text-muted-foreground"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@company.com"
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-foreground" htmlFor="invite-admin">
        <input
          id="invite-admin"
          type="checkbox"
          checked={makeAdmin}
          onChange={(e) => setMakeAdmin(e.target.checked)}
        />
        Make this user an admin
      </label>

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={!canSubmit}
          className="rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
        >
          Send invite
        </button>
        <span className="text-xs text-muted-foreground">
          They get a temporary password that expires in {validityDays} days.
        </span>
      </div>
    </form>
  )
}
